import {MeshNoteState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshNoteState';
import {MeshSheetSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshSheetSize';
import {MeshSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshSize';
import {MeshStateDetails} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshStateDetails';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {
    DEFAULT_COL_WIDTH,
    DEFAULT_NUM_COLS,
    DEFAULT_NUM_ROWS,
    DEFAULT_ROW_HEIGHT
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotConstants';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {action, computed, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {IMeshStateDesignUIState} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignUIState';

export interface IMeshStateSnapshot
{
    sheetId:SheetId;
    colWidths:number[];
    rowHeights:number[];
    isGridlinesVisible:boolean;
}

export class MeshState implements IMeshStateDesignUIState
{
    public sheet:AnySheet;

    @observable
    public colWidths:number[] = [];

    @observable
    public rowHeights:number[] = [];

    @observable
    public isGridlinesVisible:boolean = true;

    @observable
    public isHovered:boolean = false;

    @observable
    public details:MeshStateDetails = new MeshStateDetails(this);

    @observable
    public note:MeshNoteState = new MeshNoteState();

    private constructor()
    {
    }

    public static create(sheet:AnySheet,
                         numCols:number = DEFAULT_NUM_COLS,
                         numRows:number = DEFAULT_NUM_ROWS):MeshState
    {
        const meshState:MeshState = new MeshState();

        meshState.sheet = sheet;
        meshState.colWidths = new Array(numCols).fill(DEFAULT_COL_WIDTH);
        meshState.rowHeights = new Array(numRows).fill(DEFAULT_ROW_HEIGHT);

        return meshState;
    }

    @computed
    public get size():MeshSheetSize
    {
        const numRows:number = this.rowHeights.length;
        const numCols:number = this.colWidths.length;

        return new MeshSheetSize(numRows, numCols);
    }

    @computed
    public get totalColsRowsSize():MeshSize
    {
        if( !this.colWidths || !this.rowHeights )
            return MeshSize.from(0, 0);

        let totalWidth:number = 0;

        for( let i = 0; i < this.colWidths.length; i++ )
            totalWidth += this.colWidths[i];

        let totalHeight = 0;

        for( let i = 0; i < this.rowHeights.length; i++ )
            totalHeight += this.rowHeights[i];

        return MeshSize.from(totalWidth, totalHeight);
    }

    @computed
    public get geometricSize():MeshSize
    {
        // total columns width + width after end + width of MeshScrollContent
        // total rows height + height after end + height of MeshScrollContent
        return MeshSize.from(
            this.totalColsRowsSize.width + this.afterEndPartWidth + 1,
            this.totalColsRowsSize.height + this.afterEndPartHeight + 1
        );
    }

    @computed
    public get visibleStartColIndex():number
    {
        if( !this.colWidths || !this.details || this.details.scrollLeft === 0 )
            return 0;

        let totalWidth:number = 0;
        let colIndex:number = 0;

        for( let i = 0; i < this.colWidths.length; i++ )
        {
            totalWidth += this.colWidths[i];

            if( this.details.scrollLeft < totalWidth )
            {
                colIndex = i;
                break;
            }
        }

        return colIndex;
    }

    @computed
    public get visibleEndColIndex():number
    {
        if( !this.colWidths || !this.details )
            return 0;

        let totalWidth:number = 0;
        let colIndex:number = this.colWidths.length;

        for( let i = this.visibleStartColIndex; i < this.colWidths.length; i++ )
        {
            totalWidth += this.colWidths[i];

            if( this.details.visibleWidth < totalWidth )
            {
                colIndex = i;
                break;
            }
        }

        return Math.min(colIndex, this.colWidths.length - 1);
    }

    @computed
    public get visibleStartRowIndex():number
    {
        if( this.details.fixedRowIndex !== null )
            return this.details.fixedRowIndex;

        if( !this.rowHeights || this.details.scrollTop === 0 )
            return 0;

        let totalHeight:number = 0;
        let rowIndex:number = 0;

        for( let i = 0; i < this.rowHeights.length; i++ )
        {
            totalHeight += this.rowHeights[i];

            if( this.details.scrollTop < totalHeight )
            {
                rowIndex = i;
                break;
            }
        }

        return rowIndex;
    }

    @computed
    public get visibleEndRowIndex():number
    {
        if( this.details.fixedRowIndex !== null )
            return this.details.fixedRowIndex;

        if( !this.rowHeights )
            return 0;

        let totalHeight:number = 0;
        let rowIndex:number = this.rowHeights.length;

        for( let i = this.visibleStartRowIndex; i < this.rowHeights.length; i++ )
        {
            totalHeight += this.rowHeights[i];

            if( this.details.visibleHeight < totalHeight )
            {
                rowIndex = i;
                break;
            }
        }

        return Math.min(rowIndex, this.rowHeights.length - 1);
    }

    @computed
    public get alignedScrollLeft():number
    {
        if( !this.colWidths || this.details.scrollLeft === 0 )
            return 0;

        let totalWidth:number = 0;

        for( let i = 0; i < this.visibleStartColIndex; i++ )
            totalWidth += this.colWidths[i];

        return totalWidth;
    }

    @computed
    public get alignedScrollTop():number
    {
        if( !this.rowHeights || (this.details.scrollTop === 0 && this.details.fixedRowIndex === null) )
            return 0;

        let totalHeight:number = 0;

        for( let i = 0; i < this.visibleStartRowIndex; i++ )
            totalHeight += this.rowHeights[i];

        return totalHeight;
    }

    @computed
    public get afterEndPartWidth():number
    {
        let widthOfLastVisibleColumns:number = 0;
        let i:number = this.colWidths.length - 1;

        for( i; widthOfLastVisibleColumns < this.details.visibleWidth && i >= 0; i-- )
        {
            widthOfLastVisibleColumns += this.colWidths[i];
        }

        return widthOfLastVisibleColumns &&
            this.details.visibleWidth - (widthOfLastVisibleColumns - this.colWidths[i + 1]);
    }

    @computed
    public get afterEndPartHeight():number
    {
        let heightOfLastVisibleRows:number = 0;
        let i:number = this.rowHeights.length - 1;

        for( i; heightOfLastVisibleRows < this.details.visibleHeight && i >= 0; i-- )
        {
            heightOfLastVisibleRows += this.rowHeights[i];
        }

        return heightOfLastVisibleRows &&
            this.details.visibleHeight - (heightOfLastVisibleRows - this.rowHeights[i + 1]);
    }

    public clone(sheet:AnySheet):MeshState
    {
        const meshState:MeshState = new MeshState();

        meshState.sheet = sheet;
        meshState.colWidths = this.colWidths;
        meshState.rowHeights = this.rowHeights;
        meshState.isGridlinesVisible = this.isGridlinesVisible;

        return meshState;
    }

    public rowExists(rowIndex:number):boolean
    {
        return rowIndex < this.size.numRows;
    }

    public colExists(colIndex:number):boolean
    {
        return colIndex < this.size.numCols;
    }

    @action
    public addColumns(columnWidth:number, columnsCount:number):void
    {
        this.colWidths.push(...new Array(columnsCount).fill(columnWidth));
    }

    @action
    public insertColumnWidths(insertIndex:number, columnWidth:number, columnsCount:number):void
    {
        if( insertIndex < 0 || insertIndex > this.colWidths.length )
        {
            mk.error(area.Slot, `insertColumns: Invalid insertIndex: ${insertIndex}`);
            return;
        }

        this.colWidths.splice(
            insertIndex,
            0,
            ...new Array(columnsCount).fill(columnWidth)
        );
    }

    @action
    public removeColumnWidths(removeIndex:number, columnsCount:number):void
    {
        if( removeIndex < 0 || removeIndex >= this.colWidths.length )
        {
            mk.error(area.Slot, `removeColumns: Invalid removeIndex: ${removeIndex}`);
            return;
        }

        this.colWidths.splice(removeIndex, columnsCount);
    }

    @action
    public addRows(rowHeight:number, rowsCount:number):void
    {
        this.rowHeights.push(...new Array(rowsCount).fill(rowHeight));
    }

    @action
    public insertRowHeights(insertIndex:number, rowHeight:number, rowsCount:number):void
    {
        if( insertIndex < 0 || insertIndex > this.rowHeights.length )
        {
            mk.error(area.Slot, `insertRows: Invalid insertIndex: ${insertIndex}`);
            return;
        }

        this.rowHeights.splice(
            insertIndex,
            0,
            ...new Array(rowsCount).fill(rowHeight)
        );
    }

    @action
    public removeRowHeights(removeIndex:number, rowsCount:number):void
    {
        if( removeIndex < 0 || removeIndex >= this.rowHeights.length )
        {
            mk.error(area.Slot, `removeRows: Invalid removeIndex: ${removeIndex}`);
            return;
        }

        this.rowHeights.splice(removeIndex, rowsCount);
    }

    @action
    public setColWidth(colIndex:number, colWidth:number):void
    {
        if( colIndex < 0 || colIndex >= this.colWidths.length )
        {
            mk.error(area.Slot, `setColWidth: Invalid colIndex: ${colIndex}`);
            return;
        }

        if( colWidth < 0 )
        {
            mk.error(area.Slot, `setColWidth: Invalid colWidth: ${colWidth}`);
            return;
        }

        this.colWidths[colIndex] = colWidth;
    }

    @action
    public setRowHeight(rowIndex:number, rowHeight:number):void
    {
        if( rowIndex < 0 || rowIndex >= this.rowHeights.length )
        {
            mk.error(area.Slot, `setRowHeight: Invalid rowIndex: ${rowIndex}`);
            return;
        }

        if( rowHeight < 0 )
        {
            mk.error(area.Slot, `setRowHeight: Invalid rowHeight: ${rowHeight}`);
            return;
        }

        this.rowHeights[rowIndex] = rowHeight;
    }

    @action
    public setResizingCol(colIndex:number, colWidth:number):void
    {
        this.details.resizingColIndex = colIndex;
        this.details.resizingColWidth = colWidth;
    }

    public resetResizingCol():void
    {
        this.details.resizingColIndex = null;
        this.details.resizingColWidth = null;
    }

    @action
    public setResizingRow(rowIndex:number, rowHeight:number):void
    {
        this.details.resizingRowIndex = rowIndex;
        this.details.resizingRowHeight = rowHeight;
    }

    @action
    public resetResizingRow():void
    {
        this.details.resizingRowIndex = null;
        this.details.resizingRowHeight = null;
    }

    @action
    public setIsGridlinesVisible(value:boolean)
    {
        this.isGridlinesVisible = value;
    }

    public static toJson:ITransformer<MeshState, IMeshStateSnapshot> = createTransformer((meshState:MeshState) =>
    {
        return {
            sheetId: meshState.sheet.id,
            colWidths: meshState.colWidths.slice(),
            rowHeights: meshState.rowHeights.slice(),
            isGridlinesVisible: meshState.isGridlinesVisible
        };
    });

    public static fromJson(json:IMeshStateSnapshot,
                           sheets:Map<SheetId, AnySheet>,
                           currentState?:MeshState,
                           currentJson?:IMeshStateSnapshot,
                           ctx?:DesignStateFromJsonCtx):MeshState
    {
        if( !currentState )
            return MeshState.fromJsonDeep(json, sheets);

        if( !currentJson )
            currentJson = MeshState.toJson(currentState);

        const meshState:MeshState = MeshState.fromJsonDeep(json, sheets);

        MeshState._restoreUiState(meshState, currentState);

        return meshState;
    }

    public static fromJsonDeep(json:IMeshStateSnapshot, sheets:Map<SheetId, AnySheet>):MeshState
    {
        const meshState:MeshState = new MeshState();

        meshState.sheet = sheets.get(json.sheetId);
        meshState.colWidths = json.colWidths;
        meshState.rowHeights = json.rowHeights;
        meshState.isGridlinesVisible = json.isGridlinesVisible;

        return meshState;
    }

    private static _restoreUiState(state:MeshState, currentState:MeshState):void
    {
        state.details = currentState.details;
        state.note = currentState.note;

        state.details.setMeshState(state);
    }

    public toDesignUIState():IMeshStateDesignUIState
    {
        return {
            isGridlinesVisible: this.isGridlinesVisible,
            details: this.details.toDesignUIState()  
        }
    }

    @action
    public fromDesignUIState(state:IMeshStateDesignUIState)
    {
        this.isGridlinesVisible = state.isGridlinesVisible;
        this.details.fromDesignUIState(state.details);
    }
}
